import React from "react";
import { Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Popper from "@mui/material/Popper";
import CheckIcon from "@mui/icons-material/Check";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import { Box } from "@mui/system";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { type TimeframeOption } from "../Performance";
import { type OutputMetaField } from "../../../../../helpers/bizDevelopmentDashboardHelpers";

import PerformanceSortBy from "./PerformanceSortBy";
import PerformanceDataComparison from "./PerformanceDataComparison";

interface PerformanceDateFilterProps {
  timeframeOptions: TimeframeOption[];
  selectedTimeframe: TimeframeOption;
  onTimeframeChange: (opt: TimeframeOption) => void;
  additionalOptions: TimeframeOption[];
  selectedAdditional: TimeframeOption | null;
  onAdditionalChange: (opt: TimeframeOption | null) => void;
  onRefresh: () => void;
  onDownload: () => void;
  brandCount: number;
  isMtd: boolean;
  isComparisonActive: boolean;
  sortKey: string;
  sortDir: "asc" | "desc";
  onSortApply: (key: string, dir: "asc" | "desc") => void;
  outputMetadata: Record<string, OutputMetaField>;
}

const fmtDate = (d: dayjs.Dayjs) =>
  d.toDate().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

const getPrimaryLegendLabel = (tf: TimeframeOption): string => {
  switch (tf.value) {
    case "yesterday":
      return `Yesterday: ${fmtDate(dayjs().subtract(1, "day"))}`;
    case "two_days_ago":
      return `2 Days Ago: ${fmtDate(dayjs().subtract(2, "day"))}`;
    case "three_days_ago":
      return `3 Days Ago: ${fmtDate(dayjs().subtract(3, "day"))}`;
    case "mtd":
      return "MTD";
    default:
      return tf.label;
  }
};

const getComparisonLegendLabel = (tf: TimeframeOption): string => {
  switch (tf.value) {
    case "yesterday":
      return `Yesterday: ${fmtDate(dayjs().subtract(1, "day"))}`;
    case "two_days_ago":
      return `2 Days Ago: ${fmtDate(dayjs().subtract(2, "day"))}`;
    case "three_days_ago":
      return `3 Days Ago: ${fmtDate(dayjs().subtract(3, "day"))}`;
    default:
      return tf.label;
  }
};

const PerformanceDateFilter = ({
  timeframeOptions,
  selectedTimeframe,
  onTimeframeChange,
  additionalOptions,
  selectedAdditional,
  onAdditionalChange,
  onRefresh,
  onDownload,
  brandCount,
  isMtd,
  isComparisonActive,
  sortKey,
  sortDir,
  onSortApply,
  outputMetadata,
}: PerformanceDateFilterProps) => {
  const handleChange = (
    _: React.SyntheticEvent,
    newValue: TimeframeOption | null,
  ) => {
    if (newValue) onTimeframeChange(newValue);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: spacing.Spacing.xs.$value }}
      >
        <Autocomplete
          data-tracker="805"
          size="small"
          disableClearable
          options={timeframeOptions}
          value={selectedTimeframe}
          onChange={handleChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          PopperComponent={(props) => (
            <Popper
              {...props}
              placement="bottom-start"
              modifiers={[
                { name: "preventOverflow", enabled: false },
                { name: "flip", enabled: false },
              ]}
              sx={{ width: "calc(100vw - 32px) !important", zIndex: 1300 }}
            />
          )}
          sx={{
            width: "228px",
            "& .MuiInputBase-input": {
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              color: colors.GrayLightMode[700].$value,
              padding: "0px !important",
            },
            "& .MuiOutlinedInput-root": {
              height: "32px",
              padding: "6px 8px !important",
              borderRadius: spacing.Radius.md.$value,
              "&.Mui-focused fieldset": {
                borderColor: colors.GrayLightMode[300].$value,
                borderWidth: "1px",
              },
            },
          }}
          renderOption={(props, option, { selected }) => (
            <li
              {...props}
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: spacing.Spacing["5xl"].$value,
                position: "relative",
                backgroundColor: selected ? colors.Brand[50].$value : "",
              }}
            >
              {selected && (
                <CheckIcon
                  fontSize="small"
                  sx={{ position: "absolute", left: spacing.Spacing.lg.$value }}
                />
              )}
              {option.shortLabel ?? option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              color="secondary"
              placeholder="Select Period"
              inputProps={{ ...params.inputProps, readOnly: true }}
            />
          )}
        />
        <Box>
          <PerformanceDataComparison
            additionalOptions={additionalOptions}
            selectedAdditional={selectedAdditional}
            onApply={onAdditionalChange}
            isMtd={isMtd}
            isComparisonActive={isComparisonActive}
          />
          <IconButton data-tracker="811" color="secondary" size="large" onClick={onRefresh}>
            <RefreshIcon sx={{ color: colors.GrayLightMode[700].$value }} />
          </IconButton>
        </Box>
      </Stack>

      {isComparisonActive && selectedAdditional && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {!isMtd && (
            <>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: spacing.Spacing.lg.$value,
                  height: spacing.Spacing.lg.$value,
                  borderRadius: spacing.Radius.xs.$value,
                  background: colors.GrayLightMode[950].$value,
                }}
              />
              <Typography
                sx={{
                  fontSize: typography.Size.Text.xxs.$value,
                  color: "#000000DE",
                }}
              >
                {getPrimaryLegendLabel(selectedTimeframe)}
              </Typography>
            </>
          )}
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: spacing.Spacing.lg.$value,
              height: spacing.Spacing.lg.$value,
              borderRadius: spacing.Radius.xs.$value,
              background: colors.GrayLightMode[500].$value,
              ml: !isMtd ? spacing.Spacing.lg.$value + "!important" : undefined,
            }}
          />
          <Typography
            sx={{
              fontSize: typography.Size.Text.xxs.$value,
              color: "#000000DE",
            }}
          >
            {getComparisonLegendLabel(selectedAdditional)}
          </Typography>
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing.md.$value,
          color: colors.GrayLightMode[900].$value,
        }}
      >
        <Box>
          <Typography
            display={"flex"}
            alignItems={"center"}
            flexDirection={"row"}
            gap={1}
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: typography.Font_weight.Regular.$value,
            }}
          >
            Sort by:{" "}
            <PerformanceSortBy
              sortKey={sortKey}
              sortDir={sortDir}
              onApply={onSortApply}
              outputMetadata={outputMetadata}
            />
          </Typography>
          <Typography
            sx={{
              fontSize: typography.Size.Text.xs.$value,
              mt: spacing.Spacing.sm.$value,
            }}
          >
            {brandCount} Brands
          </Typography>
        </Box>
        <IconButton data-tracker="810" color="secondary" size="large" onClick={onDownload}>
          <FileDownloadIcon sx={{ color: colors.GrayLightMode[700].$value }} />
        </IconButton>
      </Stack>
    </>
  );
};

export default PerformanceDateFilter;
